/**
 * FCM Token Registration Hook
 * Firebase Cloud Messaging 토큰 등록 및 관리
 */

import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { getMessaging, getToken, onMessage, Messaging } from "firebase/messaging";
import { initializeApp, getApps, FirebaseApp } from "firebase/app";
import { firebaseConfig } from "@/lib/firebase";

const API_BASE = import.meta.env.VITE_API_BASE || "";

interface UseFCMTokenOptions {
  vapidKey?: string;
  autoRegister?: boolean;
}

export function useFCMToken(options: UseFCMTokenOptions = {}) {
  const { autoRegister = true, vapidKey } = options;
  const { user } = useAuth();
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [messaging, setMessaging] = useState<Messaging | null>(null);

  // Initialize Firebase Messaging
  useEffect(() => {
    if (typeof window === "undefined" || !("Notification" in window)) {
      setError("브라우저가 알림을 지원하지 않습니다");
      return;
    }

    if (!("serviceWorker" in navigator)) {
      setError("Service Worker를 지원하지 않습니다");
      return;
    }

    try {
      // Get or initialize Firebase app
      let firebaseApp: FirebaseApp;
      const apps = getApps();
      if (apps.length === 0) {
        firebaseApp = initializeApp(firebaseConfig);
      } else {
        firebaseApp = apps[0];
      }

      // Get messaging instance
      const messagingInstance = getMessaging(firebaseApp);
      setMessaging(messagingInstance);

      // Listen for foreground messages
      onMessage(messagingInstance, (payload) => {
        console.log("[FCM] Foreground message received:", payload);
        // Show notification manually in foreground
        if (payload.notification) {
          new Notification(payload.notification.title || "새 메시지", {
            body: payload.notification.body,
            icon: payload.notification.icon || "/icon-192x192.png",
            badge: "/badge-72x72.png",
            tag: payload.data?.chatId || "chat",
            data: payload.data,
          });
        }
      });
    } catch (err) {
      console.error("[FCM] Initialization error:", err);
      setError(err instanceof Error ? err.message : "FCM 초기화 실패");
    }
  }, []);

  // Register FCM token
  const registerToken = useCallback(async () => {
    if (!user || !messaging) {
      setError("로그인이 필요하거나 FCM이 초기화되지 않았습니다");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Request notification permission
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        throw new Error("알림 권한이 거부되었습니다");
      }

      // Get FCM token
      const fcmToken = await getToken(messaging, {
        vapidKey: vapidKey || import.meta.env.VITE_FCM_VAPID_KEY,
      });

      if (!fcmToken) {
        throw new Error("FCM 토큰을 받을 수 없습니다");
      }

      setToken(fcmToken);

      // Register token on server
      const authToken = await user.getIdToken();
      const deviceInfo = {
        platform: navigator.platform,
        browser:
          "userAgentData" in navigator
            ? (navigator.userAgentData as { brands?: Array<{ brand: string }> })
                .brands?.[0]?.brand || "unknown"
            : "unknown",
        userAgent: navigator.userAgent,
      };

      const response = await fetch(`${API_BASE}/api/chat/fcm/subscribe`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          token: fcmToken,
          device_info: deviceInfo,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "토큰 등록 실패");
      }

      console.log("[FCM] Token registered successfully");
    } catch (err) {
      console.error("[FCM] Registration error:", err);
      setError(err instanceof Error ? err.message : "토큰 등록 실패");
      setToken(null);
    } finally {
      setLoading(false);
    }
  }, [user, messaging, vapidKey]);

  // Auto-register on mount if enabled
  useEffect(() => {
    if (autoRegister && user && messaging && !token && !loading) {
      registerToken();
    }
  }, [autoRegister, user, messaging, token, loading, registerToken]);

  return {
    token,
    loading,
    error,
    registerToken,
    isSupported: messaging !== null && "Notification" in window,
  };
}
